<template>
  <v-card>
    <v-overlay :absolute="absolute" :value="!existingPromo">
      <v-row align="center">
        <v-btn fab x-small color="white" class="mr-2">
          <v-icon color="orange" x-large>mdi-alert-circle</v-icon>
        </v-btn>
        <h3>Save Promotion Details to Add Parties and Items</h3>
      </v-row>
    </v-overlay>
    <v-toolbar flat color="#fff">
      <v-btn-toggle 
        dense
        v-model="addStores"
        mandatory
        class="btnGroup">
        <v-btn
          height="38"
          :value="false"
          style="border-right:1px solid #9e9e9e;">
          <small>{{ tenant === 'alliance-retail-group' ? 'Selected': 'All'}}</small>
          <v-icon class="ml-1" small>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn
          height="38"
          v-show="id && (tenant === 'alliance-retail-group' || !readOnlyPromo)"
          :value="true"
          style="border-top-right-radius:0px;border-bottom-right-radius:0px;">
          <small>{{ tenant === 'alliance-retail-group' ? 'All': 'Add'}}</small>
          <v-icon class="ml-1" small>mdi-plus</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-col v-show="addStores" cols="3" class="pl-0" style="height:64px;">
        <v-text-field
          v-model="search"
          label="Search"
          style="border-top-left-radius:0px;border-bottom-left-radius:0px;"
          append-icon="mdi-window-close"
          @click:append="clearPartySearch"
          background-color="#fff"
          outlined
          dense>
          <template v-slot:prepend-inner>
            <v-icon>mdi-magnify</v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col v-show="addStores && $auth.tenant === 'alliance-retail-group'" cols="1" class="pl-0" style="height:64px;">
        <v-autocomplete
          v-model="storeInclusionFilter"
          label="Inclusion Filter"
          :items="inclusionFilterOptions"
          clearable
          hide-details="auto"
          background-color="#fff"
          dense
          autocomplete="off"
          outlined>
        </v-autocomplete>
      </v-col>
      <v-col v-show="!addStores" cols="3" class="pl-0" style="height:64px;">
        <v-text-field
          v-model="filter"
          label="Filter"
          style="border-top-left-radius:0px;border-bottom-left-radius:0px;"
          append-icon="mdi-window-close"
          @click:append="filter = ''"
          background-color="#fff"
          outlined
          dense>
          <template v-slot:prepend-inner>
            <v-icon>mdi-filter-outline</v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-spacer/>
      <v-btn
        v-if="selectedStores.length > 0 && !addStores"
        color="red"
        class="white--text"
        @click="handleRemoveClick">
        <v-icon left dense small>mdi-trash-can-outline</v-icon>
        Remove Selected Groups ({{ selectedStores.length }})
      </v-btn>
      <v-btn
        v-if="tenant === 'alliance-retail-group' && id && !editIsDisabled && addStores"
        :disabled="disableAddAllAdGroups"
        :inert="loading"
        color="success"
        @click="addAllAdGroupsModal = true">
        <v-icon left dense small>mdi-plus</v-icon>
        All Ad Groups
      </v-btn>
    </v-toolbar>
    <v-divider/>

  <!-- Promo Parties Table -->
    <v-data-table
      v-if="!addStores"
      :height="tableSize"
      :loading="loading"
      :headers="headers"
      :items.sync="stores"
      :search="filter"
      :items-per-page="storePageSize"
      :item-class="getRowClass"
      color="#fff"
      item-key="id"
      loading-text="Loading...Please wait"
      fixed-header
      hide-default-footer
      dense>

      <template v-slot:no-data>
        <v-alert
          v-if="stores.length < 1"
          outlined
          text
          color="orange"
          class="mt-4">
          <template v-slot:default>
            <v-icon color="orange" class="mx-2 pb-1">mdi-alert-circle-outline</v-icon>
            <span>Valid promotion requires at least one party added to promotion.</span>
          </template>
        </v-alert>
      </template>

      <template v-slot:[`item.name`]="{ item }">
       <div>
        {{ item.store_num ? "#" : "" }}{{ item.store_num }} {{ item.name }}
       </div>
      </template>

      <template v-slot:[`header.select`]>
        <v-simple-checkbox
          dark
          v-model="includeAll"
          :disabled="stores.length === 0"
          dense
          :ripple="false">
        </v-simple-checkbox>
      </template>

      <!-- Edit Lump Sum -->
      <template v-slot:[`item.lump_sum`]="{ item }">
        <template v-if="showAllowanceField(item)">
        <v-menu
          v-model="editing"
          :close-on-content-click="false"
          :close-on-click="false"
          bottom
          right
          absolute
          nudge-top="-12px"
          nudge-left="16px"
          offset-y
          max-width="200px">
          <template v-slot:activator="{ on, attrs }">
            <div class="edit-allowance-container"
              :style="!editIsDisabled ? 'cursor: pointer' : 'cursor: default'"
              v-on="!editIsDisabled ? on : null"
              v-bind="attrs"
              @click="!editIsDisabled ? open(item) : null">
              <div style="margin: 0 auto">
                <span style="display: inline-block">
                  $ {{ item.lump_sum }}
                </span>
                <v-icon style="margin-left: 10px" small v-if="!editIsDisabled">
                  mdi-pencil
                </v-icon>
              </div>
            </div>
          </template>
          <v-card class="pt-2 px-4" tile flat
            v-if="editItem && editItem.store_id === item.store_id">
            <div style="margin-top: 8px">
              <span class="subtitle-2">{{tenant === 'pricechopper' ? 'Edit Ad Fee' : 'Edit Lump Sum'}}</span>
            </div>
            <v-text-field
              ref="allowance"
              class="pt-0 mt-0"
              v-model="editItem.lump_sum"
              :rules="[(v) => Boolean(v) || 'Please enter a value', isValidAllowancePrice]"
              autofocus
              type="number"
              hide-spin-buttons
              prefix="$">
            </v-text-field>
            <v-card-actions class="pt-0 px-0">
              <v-spacer></v-spacer>
              <v-btn
                text
                small
                @click="cancel">
                Cancel
              </v-btn>
              <v-btn
                text
                small
                color="primary"
                :loading="savingItem"
                @click="save">
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        </template>
      </template>

      <!-- View Promo Party Stores/Members -->
      <template v-slot:[`item.view`]="{ item }">
        <v-tooltip top
          v-if="item && item.party_type_name && item.party_type_name.toLowerCase() !== 'store'">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              color="#1976d2"
              @click="viewParties(item)">
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
          </template>
          <span>View / Exclude</span>
        </v-tooltip>
      </template>

      <!-- Include/Exclude Promo Party -->
      <template v-slot:[`item.include_store`]="{ item }">
        <v-icon :color="item.include_store ? 'green' : 'red'">
          {{ (item.include_store) ? 'mdi-checkbox-marked-circle-outline' : ' mdi-close-circle-outline'}}
        </v-icon>
      </template>

      <!-- Delete Promo Party -->
      <template v-slot:[`item.select`]="{ item }">
        <v-simple-checkbox 
          v-model="item.selected"
          color="primary"
          dense
          @input="itemSelected($event, item)"
          :ripple="false"
          >
        </v-simple-checkbox>
      </template>
    </v-data-table>

  <!-- Party Search Table -->
    <v-data-table v-else
      id="partiesList"
      :height="tableSize"
      :headers="headers"
      :items.sync="filteredParties"
      :loading="loading"
      loading-text="Loading...Please wait"
      :items-per-page="storePageSize"
      item-key="party_id"
      fixed-header
      hide-default-footer
      dense>

      <!-- View Parties -->
      <template v-slot:[`item.view`]="{ item }">
        <v-tooltip v-if="item.party_type.toLowerCase() !== 'store'" top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" icon small color="#1976d2" @click="viewParties(item)">
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
          </template>
          <span>View</span>
        </v-tooltip>
      </template>

      <!-- Add Party -->
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-if="editIsDisabled" :color="item.disabled ? 'green' : 'red'">
              {{ (item.disabled) ? 'mdi-checkbox-marked-circle-outline' : ' mdi-close-circle-outline'}}
            </v-icon>
            <v-btn
              v-else
              v-on="on"
              :disabled="item.disabled"
              :id="$auth.tenant === 'alliance-retail-group' ? 'arg-disabled-button' : ''"
              :color="$auth.tenant === 'alliance-retail-group' ? 'grey' : 'green'"
              icon
              small
              :inert="loading && !item.loading"
              :loading="item.loading"
              @click="add(item)">
              <v-icon>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
          <span>Add</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <ViewParties
      v-if="viewPartiesModal"
      v-model="viewPartiesModal"
      @initStores="initPromoStoreList"
      :addStores="addStores"
      :promo="promo"
      :stores="stores"
      :promoAllowances="promoAllowances"
      :party="selectedParty"
      :readOnlyPromo="editIsDisabled"
      :downstreamPartyIds="downstreamPartyIds"
      @close="closeViewParties"
      @addParty="add"
      @addGroupMembers="addGroupMembers"
      @removeGroup="removeStoreConfirm">
    </ViewParties>

    <v-dialog
      v-if="removeDialog"
      justify="center"
      v-model="removeDialog"
      persistent
      width="500">
      <v-card class="ma-0 pa-6">
        <v-row justify="center">
          <v-icon x-large color="orange">mdi-alert-circle-outline</v-icon>
        </v-row>
        <v-row justify="center" class="py-6">
          <h3 v-if="includeAll && selectedStores.length === stores.length">Remove All Stores?</h3>
          <h3 v-else>{{ `Remove Selected Groups/Stores${excludedStores.length > 0 ? ` and/or exclusions from ${excludedStores.map(s => s.name).join(", ")}`:''}?`}}</h3>
          <div v-if="isApprovedPromo && hasApprovedPromoStoreEditAccess" style="margin-top: 25px">
            <v-alert
              text
              type="error"
              border="left">
              <p>
                <strong>Warning: Removing ad groups from approved promotions may impact existing ad planners and/or other downstream processes. Continue?</strong>
              </p>
            </v-alert>
          </div>
          <div v-if="lastPartySources.length > 0" style="margin-top: 25px">
            <v-alert
              text
              type="warning"
              border="left">
              <template v-if="lastPartySources.length > 1">
                <p>Items with any of the following sources will also be removed from this promotion:</p>
                <p><strong>{{ lastPartySources.join(', ') }}</strong></p>
              </template>
              <template v-else>
                Any items with the source <strong>{{ lastPartySources[0] }}</strong> will also be removed from this promotion.
              </template>
            </v-alert>
          </div>
        </v-row>
        <v-row justify="center" class="pb-4 pt-2">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                small
                color="red"
                class="elevation-3 white--text"
                @click.stop="handleRemoveDialogClose">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Cancel</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                small
                color="green"
                class="white--text elevation-3 ml-2"
                @click="removeGroup">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </template>
            <span>Confirm</span>
          </v-tooltip>
        </v-row>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="addAllAdGroupsModal"
      justify="center"
      v-model="addAllAdGroupsModal"
      persistent
      width="500"
      @click:outside="addAllAdGroupsModal = false">
      <v-card>
        <v-card-title class="pl-6 pr-2 pt-4 pb-0">
          <div class="title h6">
            Add All Ad Groups
          </div>
        </v-card-title>
        <v-container fluid class="pa-0">
          <div class="mx-6 mt-4 mb-6">
            Are you sure that you would like to add all available ad groups?
          </div>
          <div class="d-flex justify-center">
            <v-btn class="mb-4 mr-4" text @click="addAllAdGroupsModal = false">
              Cancel
            </v-btn>
            <v-btn class="mb-4" color="primary" @click="addAllAdGroups()">
              Confirm
            </v-btn>
          </div>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import Search from '@/axios/search-endpoint'
import PromoStore from '@/axios/promotion-store-endpoint'
import PromoAllowance from '@/axios/promo-allowance-endpoints'
import PartyRelationship from '@/axios/party-relationship-endpoint'

// Components
import ViewParties from '@/components/stores/viewParties'

// Mixins
import { fullWidthTable } from '@/mixins/table'
import { utils } from '@/mixins/utils'
import { displayAlert } from '@/mixins/alert'
import { userAccess } from '@/mixins/user-access'
import { formValidation } from '@/mixins/form-validation'

// third party helpers
import { cloneDeep, debounce } from 'lodash'

export default {
  name: 'stores-on-promo',
  data () {
    return {
      absolute: true,
      addStores: false,
      removeDialog: false,
      storeSearchInfo: false,
      viewPartiesModal: false,
      addAllAdGroupsModal: false,
      includeAll: false,
      selectedParty: {},
      search: '',
      filter: '',
      store: {},
      stores: [],
      parties: [],
      selectedStores: [],
      storePageSize: 250,
      from: 0,
      editItem: null,
      editing: false,
      savingItem: false,
      downstreamPartyIds: [],
      storeInclusionFilter: ""
    }
  },
  components: { ViewParties },
  mixins: [
    fullWidthTable,
    utils,
    displayAlert,
    userAccess,
    formValidation
  ],
  props: {
    panel_height: Number,
    promo: Object,
    partyItemSources: Array,
    upstreamPartyIds: Array,
    promoAllowances: Array,
    readOnlyPromo: {
      type: Boolean,
      default: true
    },
    showLumpSum: Boolean,
    refreshStores: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    search: {
      handler: debounce(function() {
        this.searchParties()
      }, 500)
    },
    filter: {
      handler() {
        this.includeAll = false
      }
    },
    addStores: {
      handler (newValue) {
        if (newValue) {
          this.searchParties()
        }
      }
    },
    includeAll: {
      handler (newValue) {
        const filteredStores = this.stores.filter(store => store.name.toLowerCase().includes(this.filter.toLowerCase()))
        if (newValue === false && filteredStores.some(store => !this.isSelected(store.store_id))) {
          return
        }
        
        this.stores = this.stores.flatMap(store => {
          if (store.selected !== newValue && filteredStores.includes(store)) {
            store.selected = newValue;
            if (newValue === false) {
              const index = this.selectedStores.findIndex(s => s.store_id === store.store_id);
              if (index > -1) {
                this.selectedStores.splice(index, 1)
              }
            } else {
              this.itemSelected(newValue, store)
            }
          }
          return store
        });
      }
    },
    selectedStores () {
      if (this.stores.length === this.selectedStores.length && this.stores.length > 0) {
        this.includeAll = true
      }
    },
    panel_height: {
      handler (value) {
        this.nonTableHeight = value
      } 
    },
    stores: {
      handler (newValue) {
        if (newValue?.length > 0) {
          this.$emit('enableItemsTab')
          this.$emit('updateShuttleEnabled', this.isShuttleEnabled)
        }
      },
      deep: true
    },
    refreshStores: {
      handler (newValue) {
        if (newValue) {
          this.initPromoStoreList()
        }
      }
    },
    upstreamPartyIds: {
      handler () {
        if (this.addStores) {
          this.searchParties()
        }
      },
      deep: true
    },
    activeDistributionCenters: {
      handler (newValue) {
        if (newValue?.length > 0) this.searchParties()
      }
    },
    '$route.query.id': {
      handler (newValue, oldValue) {
        if (newValue && oldValue) {
          this.initPromoStoreList()
        }
      }
    },
    'promo.audit.updated_on': {
      handler (newVal, oldVal) {
        if (newVal && oldVal) {
          if ((this.promoAllowances.length > 0) && !this.promo.lump_sum_allowance || this.promo.lump_sum_allowance == 0) {
            this.initPromoAllowances(true)
          }
        }
      }
    }
  },
  computed: {
    headers () {
      if (this.addStores) {
        return this.addHeaders
      }
      let headers = this.tableHeaders.filter(h => (!h.tenants || h.tenants.includes(this.tenant)))
      if (!this.showLumpSum || (this.tenant === 'alliance-retail-group' && !this.hasStatusAdminAccess)) {
        headers = this.tableHeaders.filter(h => h.value !== 'lump_sum')
      }
      return headers
    },
    tableHeaders () {
      return [
        { sortable: false, filterable: false, class: 'accent', show: true },
        { text: 'Group / Store', sortable: true, filterable: true, value: 'name', class: 'accent white--text', show: true },
        { text: 'Type', sortable: true, filterable: true, value: 'party_type_name', align: 'center', class: 'accent white--text', show: true },
        { text: 'Lump Sum Allowance', sortable: true, filterable: false, value: 'lump_sum', align: 'center', class: 'accent white--text', tenants: ['awg'], show: true },
        { text: 'Ad Fee', sortable: true, filterable: false, value: 'lump_sum', align: 'center', class: 'accent white--text', tenants: ['pricechopper', 'alliance-retail-group'], show: true },
        { text: 'Included / Excluded', align: 'center', sortable: true, filterable: true, value: 'include_store', class: 'accent white--text', show: true },
        { text: 'View Members', align: 'center', sortable: false, filterable: false, value: 'view', class: 'accent white--text', show: true },
        { text: 'Select', align: 'center', sortable: false, filterable: false, value: 'select', class: 'accent white--text', show: !this.editIsDisabled },
      ].filter(h => h.show)
    },
    addHeaders() {
      return [
        { sortable: false, filterable: false, class: 'accent'},
        { text: this.$auth.tenant === 'awg' ? 'Group' : 'Group / Store', sortable: true, filterable: true, value: 'party_name', align: 'left', class: 'accent white--text' },
        { text: 'Type', sortable: true, filterable: true, value: 'party_type', align: 'center', class: 'accent white--text' },
        { text: 'View Members', align: 'center', sortable: false, filterable: false, value: 'view', class: 'accent white--text' },
        { text: this.editIsDisabled ? 'Added To Promo' : 'Add', align: 'center', value: 'actions', sortable: false, filterable: false, class: 'accent white--text' }
      ]
    },
    editIsDisabled() {
      if (this.$auth.tenant === 'alliance-retail-group' && this.isApprovedPromo && this.hasApprovedPromoStoreEditAccess) {
        return false
      }
      return this.readOnlyPromo && !(this.promo?.status?.status?.constant === 'INCLUDED_IN_MERGE' && this.userRoles.some(role => ['promo.admin', 'ipro-admin', 'arg-admin', 'arg-category-manager'].includes(role)))
    },
    filteredParties() {
      if (this.storeInclusionFilter) {
        if (this.storeInclusionFilter === 'Added') {
          return this.parties.filter(p => p.disabled)
        }
        if (this.storeInclusionFilter === 'Unadded') {
          return this.parties.filter(p => !p.disabled)
        }
        if (this.storeInclusionFilter === 'Excluded') {
          return this.parties.filter(p => !p.include_store)
        }
      }
      return this.parties.filter(p => p.party_name.includes(this.filter))
    },
    isApprovedPromo() {
      return (this.promo?.status?.status?.constant === 'APPROVED')
    },
    totalPromoExcludedAdGroups() {
      return this.$store.getters.totalPromoExcludedAdGroups
    },
    adPromoOnlyExcludedAdGroups() {
      return this.$store.getters.adPromoOnlyExcludedAdGroups
    },
    excludedAdGroups() {
      return (this.promo.billing_mode.constant === "AD" && this.promo.promo_category.name != "DSD Off Invoice") 
        ? [...this.adPromoOnlyExcludedAdGroups, ...this.totalPromoExcludedAdGroups]
        : this.totalPromoExcludedAdGroups
    },
    excludedStores() {
      return this.selectedStores.filter(store => store.party_type_name.toLowerCase() === 'store' && store.include_store === false)
    },
    allExcludedStores() {
      return this.stores.filter(store => store.party_type_name.toLowerCase() === 'store' && store.include_store === false)
    },
    existingPromo () {
      return (this.$route.query.id) ?? false
    },
    isPCEVendor () {
      return this.$auth.userRoles.includes('pce-vendor')
    },
    id () {
			return (this.$route.query.id) ?? this.promo_id
    },
    shuttleParentPartyTypes () {
      return ['wholesaler', 'distribution center']
    },
    isShuttleEnabled () {
      return this.stores.every(party => {
        return this.shuttleParentPartyTypes.includes(party?.party_type_name?.toLowerCase())
      })
    },
    lastPartySources () {
      const sourceIds = this.getLastPartySources()
      return sourceIds.map(sourceId => this.$store.getters.getSourceName(sourceId))
    },
    partySearchTypes () {
      // tenant-specific logic
      if (['spn', 'jbg'].includes(this.$auth.tenant)) {
        return ['DISTRIBUTION_CENTER', 'OWNER', 'STORE', 'STORE_GROUP']
      }
      if (this.$auth.tenant === 'alliance-retail-group') {
        const types = ['AD_GROUP', 'REGION_GROUP', 'STORE', 'DISTRIBUTION_CENTER']
          if (!this.limitAccessByRelatedVendors) {
            types.push('SUBGROUP')
          }
        return types
      }
      
      if (this.isPCEVendor) {
        return ['REGION_GROUP']
      }

      // awg/default
      return ['AD_GROUP', 'REGION_GROUP']
    },
    groupPartyTypes () {
      let types = ['REGION_GROUP', 'STORE_GROUP']
      if (this.tenant === 'alliance-retail-group') {
        types.push('DISTRIBUTION_CENTER')
        if (!this.limitAccessByRelatedVendors) {
          types.push('SUBGROUP')
        }
      }
      return types
    },
    memberPartyTypes () {
      return ['STORE', 'AD_GROUP']
    },
    activeDistributionCenters () {
      return this.$store.getters.activeDistributionCenters
    },
    activeRegionGroups () {
      return this.$store.getters.activeRegionGroups
    },
    activeAdGroups () {
      return this.$store.getters.activeAdGroups
    },
    availableAdGroups () {
      if (this.activeAdGroups.length > 0) {
        return this.activeAdGroups.filter(ag => {
          return (!this.excludedAdGroups.some(g => g?.id === ag.id))
            && !this.stores.some(store => store?.store_id === ag.id)
        })
      }
      return []
    },
    availableAdGroupsIds () {
      if (this.availableAdGroups?.length > 0) {
        return this.availableAdGroups.map(ag => ag.id)
      }
      return []
    },
    disableAddAllAdGroups () {
      return this.availableAdGroupsIds.length === 0
    },
    inclusionFilterOptions() {
      return ['Added', 'Unadded', "Excluded"]
    }
  },
  created () {
    if (this.includeAll) this.includeAll = false
    this.nonTableHeight = this.panel_height
    if (this.existingPromo) {
      this.initPromoStoreList()
    }
},
  methods: {
    open (item) {
      this.editItem = cloneDeep(item)
      const numericValue = this.getNumericPrice(item.lump_sum)
      const editValue = (numericValue === 0) ? null : numericValue.toFixed(2)
      this.editItem.lump_sum = editValue
    },
    save () {
      const valid = this.$refs.allowance.validate()
      if (valid) {
        this.setAllowance(this.editItem)
      }
    },
    cancel () {
      this.editItem = null
      this.editing = false
    },
    handleRemoveClick() {
      this.removeDialog = true
    },
    handleRemoveDialogClose() {
      this.removeDialog = false
    },
    async setAllowance (promoParty) {
      const { store_id, lump_sum } = promoParty
      const payload = {
        promo_id: this.id,
        party_id: store_id,
        allowance: lump_sum
      }
      const existingAllowance = this.getPartyAllowance(store_id)
      const promise = existingAllowance
        ? PromoAllowance.put(payload)
        : PromoAllowance.post(payload)

      this.savingItem = true
      try {
        await promise
        this.editing = false
        this.editItem = null
        this.$emit('updateHistory')
        this.initPromoStoreList()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.savingItem = false
        this.loading = false
      }
    },
    getPartyAllowance (partyId) {
      return this.promoAllowances.find(p => p?.party_id === partyId)
    },
    showAllowanceField (party) {
      return this.showLumpSum && ((party?.party_type_name?.toLowerCase() === 'ad group') || (['Ad Scan Non-Allowance', 'Ad Non Allowance'].includes(this.promo.promo_category.name) && party?.party_type_name?.toLowerCase() === 'store') && party.include_store)
    },
    itemSelected (value, store) {
      if (value === true) {
        if (!this.isSelected(store.store_id)) {
          this.selectedStores.push(store)
        }
      } else {
        const index = this.selectedStores.findIndex(s => {
          return (s.store_id === store.store_id)
        })
        if (index > -1) this.selectedStores.splice(index, 1)
        if (this.includeAll) {
          this.includeAll = false
        }
      }
    },
    isSelected(id) {
      return this.selectedStores.map(s => s.store_id).includes(id)
    },
    getLastPartySources () {
      if (!this.selectedStores?.length) return [];
      
      const sourcesToRemove = new Set();
      const selectedPartyIds = new Set(this.selectedStores.map(store => store.store_id));
      
      const allSources = new Map();
      
      this.partyItemSources.forEach(partySource => {
        if (partySource.sources?.length) {
          partySource.sources.forEach(sourceId => {
            if (!allSources.has(sourceId)) {
              allSources.set(sourceId, new Set());
            }
            allSources.get(sourceId).add(partySource.party_id);
          });
        }
      });
      
        allSources.forEach((partySet, sourceId) => {
        const allParties = [...partySet];
        const allPartiesSelected = allParties.every(partyId => selectedPartyIds.has(partyId));
        
        if (allPartiesSelected && allParties.length > 0) {
          sourcesToRemove.add(sourceId);
        }
      });
      
      return [...sourcesToRemove]
    },
    getAdGroupsToAdd(groupList) {
      return groupList.filter(id => {
        const isNewStore = id && !this.stores.some(store => store?.store_id === id);
        return this.tenant === 'alliance-retail-group' 
          ? isNewStore && !this.excludedAdGroups.some(ad => ad.id === id) 
          : isNewStore;
      });
    },
    async initPromoStoreList () {
      this.loading = true
      let stores = []
      try {
        const { data } = await PromoStore.getStores(this.id, this.storePageSize, this.from)
        if (data?.length > 0) {
          const partyIds = data.map(s => s.store_id)
          this.$emit('setPromoPartyIds', partyIds)
          await this.initPromoAllowances()
          stores = this.formatPromoStores(data)
          if (this.tenant === 'pricechopper') {
            stores.sort((a, b) => {
                if (a.name === 'Cosentino Price Chopper') return -1;
                if (b.name === 'Cosentino Price Chopper') return 1;
                if (a.name === 'McKeever Price Chopper') return -1;
                if (b.name === 'McKeever Price Chopper') return 1;
                return a.name - b.name;
            })
          } else if (this.tenant === 'alliance-retail-group') {
            const excludedStores = stores.filter(p => p.party_type_name.toLowerCase() === 'store' && p.include_store === false)
            if (excludedStores.length > 0) {
              stores = await this.sortExcludedStores(stores)
            } else {
              this.sortByKey(stores, 'name')
            }
          } else {
            this.sortByKey(stores, 'name')
          }
        }
        this.stores = stores
        await this.initDownstreamPartyIds(this.stores)
        if (this.addStores) {
          this.searchParties()
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        if (!this.addStores) this.loading = false
        this.selectedStores = []
        this.includeAll = false
      }
    },
    async initDownstreamPartyIds (stores) {
      let partyIds = []
      const DCs = stores.filter(s => s?.party_type_name?.toLowerCase() === 'distribution center')
      if (DCs.length > 0) {
        const results = await this.getAllSettled(DCs.map(party => this.getMemberPartyIds(party, this.memberPartyTypes)))
        partyIds = Array.from(new Set(results))
      }
      this.downstreamPartyIds = partyIds
    },
    async initPromoAllowances (refreshStores = false) {
      const res = await PromoAllowance.getPromoAllowances(this.id)
      this.$emit('setPromoAllowances', res.data || [])
      this.$emit('getPromoStores')
      if (refreshStores) {
        this.stores = this.formatPromoStores(this.stores)
      }
    },
    formatPromoStores (parties) {
      return parties.map(party => {
        party.lump_sum = null
        if (party?.party_type_name?.toLowerCase() === 'ad group' || party?.party_type_name?.toLowerCase() === 'store') {
          const existingAllowance = this.getPartyAllowance(party.store_id)
          let lump_sum = existingAllowance
            ? existingAllowance.allowance
            : 0.00
          lump_sum = this.getNumericPrice(lump_sum)
          party.lump_sum = lump_sum.toFixed(2)
        }
        return party
      })
    },
    initParties () {
      this.searchParties()
    },
    async sortExcludedStores(parties) {
      let adGroups = this.filterAndSortAdGroups(parties)
      const sortedList = await this.getGroupMembers(adGroups, parties)
      
      const remainingStores = this.getRemainingStores(parties, sortedList)
      return [...sortedList, ...remainingStores]  
    },
    filterAndSortAdGroups(parties) {
      let groups = parties.filter(p => p.party_type_name.toLowerCase() === 'ad group')
      groups = this.sortByKey(groups, 'name')
      return groups
    },
    async getGroupMembers(adGroups, parties) {
      const sortedList = []
      const excludedStores = parties.filter(p => p.party_type_name.toLowerCase() === 'store' && !p.include_store)
      const partyIds = excludedStores.map(s => s.store_id)
      const payload = {
        from_party_ids: partyIds,
        excluded_upstream_types: ['WHOLESALER', 'REGION_GROUP', 'STORE_GROUP', 'OWNER', 'DISTRIBUTION_CENTER', 'SUBGROUP']
      }
      const response = await PartyRelationship.getUpstreamRelationships(payload)
      if (response?.data?.length > 0) {
        for (const ag of adGroups) {
          const relationships = response.data.filter(rel => rel.from_rel_type === 'PARTICIPATES_IN_ADS' && rel.to_party_id === ag.store_id)
          const members = excludedStores
            .filter(party => relationships.some(rel => rel.from_party_id === party.store_id))
            .sort((a) => {
              if (a.include_store === false) return -1
            })
            .map(child => (child.is_child = true, child))
          sortedList.push(ag, ...members)
        }
      }
      return sortedList
    },
    getRemainingStores(parties, sortedList) {
      return parties.filter(p => !sortedList.some(s => s.store_id === p.store_id))
    },
    async searchParties () {
      this.loading = true
      let parties = []
      const operator = this.search ? 'and' : 'or'
      const term = this.search || this.buildPartySearchTerm()
      try {
        let { data } = await Search.customerSearch(term, operator)
        if (this.limitAccessByRelatedStores) {
          data = data.filter(p => this.userRelatedPartyIds.includes(p?.party_id))
        }
        if (data?.length > 0) {
          parties = await this.getAllSettled(data.flatMap(async p => {
            if (this.partySearchTypes.includes(p?.party_type)) {
              if (p.party_type === 'AD_GROUP') {
                const totalExcluded = this.totalPromoExcludedAdGroups.some(ag => ag?.id === p.party_id)
                if ((this.$auth.tenant === 'alliance-retail-group' && totalExcluded)
                    || !this.activeAdGroups.some(ag => ag.id === p.party_id)) { 
                  return []
                }
              }
              if (p.party_type === 'SUBGROUP' && p.attributes?.SUBGROUP_IS_ACTIVE !== 'True') {
                return []
              }
              if (p?.party_type === 'DISTRIBUTION_CENTER') {
                // Only alliance-retail-group has DC_IS_ACTIVE attributes
                if (this.$auth.tenant === 'alliance-retail-group') {
                  const activeDcParty = this.activeDistributionCenters.find(activeDc => activeDc.party_id === p?.party_id)
                  if (!activeDcParty) {
                    return []
                  }
                  p.member_ids = activeDcParty.member_ids
                } else {
                  p.member_ids = []
                }
              }
              if (p?.party_type === 'REGION_GROUP') {
                const activeRgParty = this.activeRegionGroups.find(activeRg => activeRg.party_id === p?.party_id)
                p.member_ids = activeRgParty ? activeRgParty.member_ids : []
              }
              p.disabled = await this.isAddDisabled(p)
              p.loading = false
              return p
            }
            return []
          }))
          parties = this.getOrderedParties(parties)
        }
        parties = parties.map(p => {
          p.include_store = !this.allExcludedStores.map(s => s.store_id).includes(p.party_id)
          return p
        })
        this.parties = parties
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    getOrderedParties (parties) {
      let groupParties = []
      if (this.tenant === 'alliance-retail-group') {
        const regionGroups = []
        const distributionCenters = []
        parties = parties.flatMap(party => {
          if (party?.party_type === 'REGION_GROUP') {
            regionGroups.push(party)
            return []
          } else if (party?.party_type === 'DISTRIBUTION_CENTER') {
            distributionCenters.push(party)
            return []
          }
          return [party]
        })
        this.sortByKey(regionGroups, 'party_name')
        this.sortByKey(distributionCenters, 'party_name')
        groupParties = [...regionGroups, ...distributionCenters]
      } else {
        parties = parties.flatMap(party => {
          if (this.groupPartyTypes.includes(party?.party_type)) {
            groupParties.push(party);
            return []
          }
          return [party]
        })
        this.sortByKey(groupParties, 'party_name')
      }
      this.sortByKey(parties, 'party_name')
      return [...groupParties, ...parties]
    },
    buildPartySearchTerm () {
      // initial search to populate table data
      const excludeTypes = this.$auth.tenant === 'spn' ? ['SUBGROUP'] : ['STORE', 'SUBGROUP']
      const terms = (this.limitAccessByRelatedStores)
        ? this.userRelatedPartyIds
        : this.partySearchTypes.filter(t => !excludeTypes.includes(t))
      return terms.join(' ')
    },
    clearPartySearch () {
      this.search = ''
      this.initParties()
    },
    async add (party) {
      if (this.groupPartyTypes.includes(party?.party_type)) {
        return this.addGroupMembers(party)
      }
      this.loading = true
      party.loading = true
      const payload  = {
        promo_id: this.id,
        store_id: party.party_id,
        include_store: true,
        enforce_compliance: true
      }
      try {
        await PromoStore.post(payload)
        this.$emit('updateHistory')
      } catch (err) {
        this.handleError(err)
        this.loading = false
        party.loading = false
      } finally {
        this.initPromoStoreList()
      }
    },

    async addGroupMembers (party, memberPartyIds = []) {
      this.loading = true
      party.loading = true
      if (memberPartyIds.length === 0) {
        memberPartyIds = await this.getMemberPartyIds(party, this.memberPartyTypes)
      }
      const membersToAdd = this.getAdGroupsToAdd(memberPartyIds);
      // add button should be disabled if this is true, but check anyway
      if (membersToAdd.length === 0) {
        party.loading = false
        this.loading = false
        return
      }
      const promoId = this.$route.query.id
      const promises = membersToAdd.map(partyId => {
        const payload  = {
          promo_id: promoId,
          store_id: partyId,
          include_store: true,
          enforce_compliance: true
        }
        return PromoStore.post(payload)
      })
      try {
        const { rejected } = await this.getAllSettled(promises, true)
        if (rejected.length > 0) throw rejected
        this.$emit('updateHistory')
        if (this.$auth.tenant === 'alliance-retail-group' && (this.isApprovedPromo && this.hasApprovedPromoStoreEditAccess)) {
          const note = `[Automated by system]: Parties were added to this approved promotion by user: ${this.$auth.user.email}. Party ids added: \n${membersToAdd.join(', ')}`
          this.$emit('addApprovedStatusNote', note)
        }
      } catch (err) {
        this.handleError(err)
        this.loading = false
        party.loading = false
      } finally {
        this.initPromoStoreList()
      }
    },
    async addAllAdGroups () {
      this.loading = true
      const adGroupsToAdd = this.getAdGroupsToAdd(this.availableAdGroupsIds);
      if (adGroupsToAdd.length === 0) return
      const promoId = this.$route.query.id
      const promises = adGroupsToAdd.map(partyId => {
        const payload  = {
          promo_id: promoId,
          store_id: partyId,
          include_store: true,
          enforce_compliance: true
        }
        return PromoStore.post(payload)
      })
      try {
        const { rejected } = await this.getAllSettled(promises, true)
        if (rejected.length > 0) throw rejected
        this.$emit('updateHistory')
        if (this.$auth.tenant === 'alliance-retail-group' && (this.isApprovedPromo && this.hasApprovedPromoStoreEditAccess)) {
          const note = `[Automated by system]: Parties were added to this approved promotion by user: ${this.$auth.user.email}. Party ids added: \n${adGroupsToAdd.join(', ')}`
          this.$emit('addApprovedStatusNote', note)
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.addAllAdGroupsModal = false
        this.initPromoStoreList()
      }
    },
    async isAddDisabled (party) {
      if (party?.member_ids && this.groupPartyTypes.includes(party?.party_type)) {
        let memberPartyIds = party?.member_ids || []
        if (memberPartyIds?.length === 0) {
          memberPartyIds = await this.getMemberPartyIds(party, this.memberPartyTypes)
        }
        if (this.totalPromoExcludedAdGroups.length > 0) {
          memberPartyIds = memberPartyIds.filter(id => !this.totalPromoExcludedAdGroups.some(ag => ag.id === id))
        }
        return (memberPartyIds.length === 0) || this.allMembersAdded(memberPartyIds)
      }
      const includedOnPromo = this.stores.some(store => store?.store_id === party.party_id)
      const isUpstream = this.upstreamPartyIds.includes(party.party_id)
      const isDownstream = this.downstreamPartyIds.includes(party.party_id)
      return includedOnPromo || (isUpstream || isDownstream)
    },
    allMembersAdded (memberPartyIds) {  
      if (memberPartyIds.length > 0) {
        return memberPartyIds.every(partyId => {
          return Boolean(this.stores.find(store => store?.store_id === partyId))
        })
      }
      return false
    },
    async update (store) {
      const promoStore = {
        'promo_id': this.id,
				'store_id': store.store_id,
				'include_store': !store.include_store,
				'enforce_compliance': store.enforce_compliance
      }
      await PromoStore.put(store.id,promoStore)
        .then(() => {
          store.include_store = !store.include_store
          this.$emit('updateHistory')
        }).catch(err => {
          this.handleError(err)
        })
      },
    // Keeping remove function for potential future reimplementation
    //   async remove () {
    //   await PromoStore.delete(this.store.id)
    //     .then(() => {
    //       this.$emit('updateItemSources', this.store.store_id)
    //       const index = this.stores.findIndex(store => store.id === this.store.id)
    //       this.stores.splice(index, 1)
    //     }).catch(err => {
    //      this.handleError(err)
    //     }).finally(() => {
    //       this.handleRemoveDialogClose()
    //       this.initPromoStoreList()
    //     })
    // },

    async removeGroup () {
      const idsToRemove = this.selectedStores.map(s => s.id)
      const removedPartyNames = this.selectedStores.map(s => s.name).join(', ')
      await PromoStore.deleteGroup(idsToRemove)
        .then(() => {
          this.selectedStores.forEach(selectedStore => {
            this.$emit('updateItemSources', selectedStore.store_id)
            const index = this.stores.findIndex(store => store.id === selectedStore.id)
            this.stores.splice(index, 1)
          })
          if (this.$auth.tenant === 'alliance-retail-group' && (this.isApprovedPromo && this.hasApprovedPromoStoreEditAccess)) {
            const note = `[Automated by system]: Parties were removed from this approved promotion by user: ${this.$auth.user.email}. \nGroups/stores removed:\n${removedPartyNames}`
            this.$emit('addApprovedStatusNote', note)
          }
        }).catch(err => {
         this.handleError(err)
        }).finally(() => {
          this.handleRemoveDialogClose()
          this.initPromoStoreList()
          this.loading = false
        })
    },
    removeStoreConfirm (store, skipConfirm) {
      this.store = store

      if (skipConfirm) {
        this.remove()
      } else {
        this.removeDialog = true
      }
    },
    viewParties (party) {
      this.selectedParty = party
      this.viewPartiesModal = true
    },
    closeViewParties (update = false) {
      this.viewPartiesModal = false
      this.selectedParty = null
      if (update) this.initPromoStoreList()
    },
  getRowClass(item) {
    return item?.is_child ? 'shade' : ''
  }
  }
}
</script>
<style>
.btnGroup {
  border-top: 1px solid #9e9e9e;
  border-left: 1px solid #9e9e9e;
  border-bottom: 1px solid #9e9e9e;
  border-top-right-radius:0px !important;
  border-bottom-right-radius:0px;
}

.shade {
  background-color: whitesmoke
}

#arg-disabled-button.theme--light.v-btn.v-btn--disabled .v-btn__content .v-icon{
    color: #4caf4fc0 !important;
}

.edit-allowance-container {
  display: grid;
  position: relative;
}
</style>